import { Tooltip } from "@mui/material";
import { createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";

export type StatusInfoType = {
    component: () => IconType;
    bg: string;
    hover: string;
    statuses: string[];
};
type ListType = { status?: string | null };

type StatusSummariesProps<T extends ListType> = {
    statuses: StatusInfoType[];
    list: T[];
    activeStatus: null | string;
    handleClick: (status: string | null) => void;
    tooltipPrefix: string;
};

function StatusSummaries<T extends ListType>({
    list,
    handleClick,
    activeStatus,
    statuses,
    tooltipPrefix,
}: StatusSummariesProps<T>) {
    const totals = useMemo(
        () =>
            list.reduce<(StatusInfoType & { count: number })[]>((prev, { status }) => {
                if (!status) return prev;

                const item = prev.find((cell) => cell.statuses.includes(status));
                if (item) item.count += 1;
                else {
                    const statusComp = statuses.find((cell) => cell.statuses.includes(status));
                    if (!statusComp) return prev;
                    prev.push({
                        ...statusComp,
                        count: 1,
                    });
                }

                return prev;
            }, []),
        [list, statuses]
    );

    return (
        <div className="flex gap-2 items-center">
            {totals.map((item) => (
                <StatusSummaryItem
                    key={item.statuses[0]}
                    item={item}
                    handleClick={handleClick}
                    activeStatus={activeStatus}
                    tooltipPrefix={tooltipPrefix}
                />
            ))}
        </div>
    );
}

function StatusSummaryItem<T extends ListType>({
    item,
    tooltipPrefix,
    handleClick,
    activeStatus,
}: Pick<StatusSummariesProps<T>, "tooltipPrefix" | "handleClick" | "activeStatus"> & {
    item: StatusInfoType & { count: number };
}) {
    const { t } = useTranslation();
    const status = item.statuses[0];
    return (
        <Tooltip key={item.statuses[0]} title={t(`${tooltipPrefix}${status}`)}>
            <button
                onClick={() => handleClick(activeStatus === status ? null : status)}
                className={
                    "p-2  max-w-fit rounded-lg hover:scale-105 active:scale-95 duration-200  items-center flex gap-1  !leading-none " +
                    (activeStatus === status ? "  border   scale-110    shadow-lg " : "")
                }
                style={{
                    backgroundColor: item.bg,
                    color: item.hover,
                    fontWeight: 500,
                    borderColor: activeStatus === status ? item.hover : "",
                }}
            >
                {createElement(item.component(), { fontSize: 14, color: item.hover })} {item.count}
            </button>
        </Tooltip>
    );
}

export default StatusSummaries;
